import AddIcon from '@mui/icons-material/Add';
import DeleteIconButton from '../components/DeleteIconButton';
import EditIcon from '@mui/icons-material/BorderColorOutlined';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, TextField, Typography } from '@mui/material';
import { green } from '@mui/material/colors';
import { makeStyles } from '@mui/styles';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import React from 'react';
import { useHistory } from 'react-router';

import { GridActionsCellItem } from '@mui/x-data-grid';
import { DataAPIContext } from '../api';
import { CREATE, DELETE, GET_ONE } from '../api/dataProvider';
import OutlinedExportButton from '../components/OutlinedExportButton';
import FixedLinearProgress from '../components/FixedLinearProgress';
import FormActionButtons from '../components/FormActionButtons';
import LicenseeFormField from '../components/LicenseeFormField';
import PageMeta from '../components/PageMeta';
import RecordList from '../components/RecordList';
import { getLicenseeColumn } from '../components/franchiseColumns';
import { ContextStore } from '../store/contextStore';
import { useIsWidthUp } from '../utils';
import useConfirmDialog from '../components/useConfirmDialog';

const useStyles = makeStyles((theme) => ({
    root: {
        backgroundColor: '#FCFCFE',
        paddingBottom: '1rem !important',
    },
    recordListWrapper: {
        [theme.breakpoints.down('sm')]: {
            height: `calc(${theme.cp.contentHeight} - 15rem) !important`,
        },
    },
    recordList: {
        border: 'none !important',
        '&.MuiDataGrid-root .MuiDataGrid-cell:focus-within': {
            outline: 'none',
        },
        height: `calc(${theme.cp.contentHeight} - 3rem) !important`,
    },
    dm_enabled: {
        color: green[800],
        fontWeight: 'bold',
    },
    dm_disabled: {
        textDecoration: 'line-through',
    },
}));

const RulesetList = (props) => {
    const [confirmDelete, confirmDeleteDialog] = useConfirmDialog();
    const history = useHistory();
    const dataAPI = React.useContext(DataAPIContext);
    const resource = 'notification_ruleset';
    const classes = useStyles();
    const [lastUpdate, setLastUpdate] = React.useState();
    const [syncActive, setSyncActive] = React.useState(false);
    const isSmallScreen = !useIsWidthUp('sm');
    const { enqueueSnackbar } = useSnackbar();
    const { hasPerm, isFranchise, licensees, currentLicensee } = React.useContext(ContextStore);
    const hasWritePerm = hasPerm('notification', 'write');
    const hasDeletePerm = hasPerm('notification', 'delete');
    const [createDialogOpen, setCreateDialogOpen] = React.useState(false);
    const [newRulesetName, setNewRulesetName] = React.useState('');
    const [newRulesetLicensee, setNewRulesetLicensee] = React.useState(currentLicensee || null);

    const onAddRuleset = () => {
        setNewRulesetName('');
        setNewRulesetLicensee(currentLicensee || null);
        setCreateDialogOpen(true);
    };

    const onCreateRulesetConfirm = () => {
        if (!newRulesetName) return;
        if (isFranchise && !newRulesetLicensee) return;
        setCreateDialogOpen(false);
        setSyncActive(true);
        const data = { name: newRulesetName };
        if (newRulesetLicensee) {
            data.tour_operator = newRulesetLicensee;
        }
        dataAPI(CREATE, resource, { data })
            .then((res) => {
                if (res.statusCode === 201) {
                    enqueueSnackbar('Ruleset created successfully', { variant: 'success' });
                    history.push(`/notifications/rulesets/${res.data.id}`);
                } else {
                    enqueueSnackbar(`Ruleset creation failed (${res.statusCode})`, { variant: 'error' });
                }
            })
            .catch((err) => {
                enqueueSnackbar(`Ruleset creation failed (${err})`, { variant: 'error' });
            })
            .finally(() => {
                setSyncActive(false);
            });
    }

    const onDeleteRuleset = async (rset_id, ev) => {
        if (await confirmDelete({ body: 'Are you sure you want to delete this ruleset?' })) {
            setSyncActive(true);
            dataAPI(DELETE, resource, { id: rset_id })
                .then((res) => {
                    if (res.statusCode === 204) {
                        enqueueSnackbar('Ruleset deleted successfully', { variant: 'success' });
                        setLastUpdate(moment());
                    } else {
                        // A ruleset still in use is refused with where it is used (406 ruleset_in_use, CP-7771).
                        enqueueSnackbar(res.data?.message || `Ruleset deletion failed (${res.statusCode})`, { variant: 'error', autoHideDuration: 8000 });
                    }
                })
                .catch((err) => {
                    enqueueSnackbar(`Ruleset deletion failed (${err})`, { variant: 'error' });
                })
                .finally(() => {
                    setSyncActive(false);
                });
        }
    }

    const onExportList = async () => {
        try {
            setSyncActive(true);
            const res = await dataAPI(GET_ONE, resource, { urlSuffix: 'export/' });
            const body = await res.response.blob();
            const url = window.URL.createObjectURL(body);

            let filename = '';
            const disposition = res.headers['Content-Disposition'];
            if (disposition && disposition.indexOf('attachment') !== -1) {
                const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
                const matches = filenameRegex.exec(disposition);
                if (matches != null && matches[1]) {
                    filename = matches[1].replace(/["']/g, '');
                }
            }
            if (!filename || filename.length <= 0) {
                filename = 'rulesets.csv';
            }

            const a = document.createElement('a');
            a.style.display = 'none';
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            setTimeout(() => {
                document.body.removeChild(a);
            }, 100);
        } catch (error) {
            console.error('onExportList: Error exporting rulesets:', error);
            enqueueSnackbar('Export failed', { variant: 'error' });
        } finally {
            setSyncActive(false);
        }
    };

    const recordListColumns = [
        ...getLicenseeColumn(isFranchise, licensees, currentLicensee),
        {
            field: 'name',
            headerName: 'Name',
            sortable: false,
            flex: 1,
            type: 'string',
        },
        {
            field: 'codename',
            headerName: 'Codename',
            sortable: false,
            filterable: false,
            flex: 0.7,
            type: 'string',
        },
        {
            field: 'rules_count',
            headerName: '# of Rules',
            sortable: false,
            filterable: false,
            flex: 0.5,
            type: 'number',
        },
        {
            field: 'updated_ts',
            headerName: 'Updated at',
            sortable: true,
            flex: 1,
            type: 'dateTime',
            valueGetter: (p) => moment(p.row.updated_ts),
            valueFormatter: (p) => p.value.format('YYYY-MM-DD HH:mm Z')
        },
        {
            field: 'actions',
            type: 'actions',
            headerName: 'Actions',
            getActions: (p) => [
                <GridActionsCellItem label={hasWritePerm ? 'Edit' : 'View'} icon={hasWritePerm ? <EditIcon /> : <VisibilityIcon />} color="primary" onClick={() => history.push(`/notifications/rulesets/${p.row.id}`)} />,
                <DeleteIconButton allowed={hasDeletePerm} onClick={(ev) => onDeleteRuleset(p.row.id, ev)} />,
            ]
        }
    ];

    return (
        <div className={classes.root}>
            {confirmDeleteDialog}
            <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Create New Ruleset</DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
                    <LicenseeFormField
                        value={newRulesetLicensee}
                        onChange={setNewRulesetLicensee}
                        error={isFranchise && !newRulesetLicensee}
                        helperText={isFranchise && !newRulesetLicensee ? 'Licensee is required' : undefined}
                    />
                    <TextField
                        autoFocus
                        required
                        variant="filled"
                        size="small"
                        label="Ruleset Name"
                        value={newRulesetName}
                        onChange={(e) => setNewRulesetName(e.target.value)}
                        fullWidth
                        onKeyDown={(e) => { if (e.key === 'Enter') onCreateRulesetConfirm(); }}
                    />
                </DialogContent>
                <DialogActions>
                    <FormActionButtons
                        onSubmit={onCreateRulesetConfirm}
                        submitDisabled={!newRulesetName || (isFranchise && !newRulesetLicensee)}
                        onCancel={() => setCreateDialogOpen(false)}
                        size="medium"
                    />
                </DialogActions>
            </Dialog>
            <FixedLinearProgress visibility={syncActive} />
            <PageMeta title="Rulesets" />
            {/* {confirmationDialogAction ? (
                <ConfirmationDialog
                    classes={{ paper: classes.paper }}
                    keepMounted
                    onCancel={confirmationDialogCancel}
                    onConfirm={confirmationDialogConfirm}
                    title={ConfirmationDialogText[confirmationDialogAction].title}
                    okButtonText={ConfirmationDialogText[confirmationDialogAction].okButtonText}
                    cancelButtonText={ConfirmationDialogText[confirmationDialogAction].cancelButtonText}
                    open={!!confirmationDialogAction}
                >
                    {confirmationDialogAction !== 'delete_notification' && confirmationDialogAction !== 'duplicate_notification' && ConfirmationDialogText[confirmationDialogAction].body}
                    {confirmationDialogAction === 'delete_notification' && (
                        <Typography>
                            {ConfirmationDialogText[confirmationDialogAction].body}
                            <span style={{ fontWeight: 700 }}>{active?.name || 'Notification'}</span> This action is irreversible. Are you sure?
                        </Typography>
                    )}
                    {confirmationDialogAction === 'duplicate_notification' && ConfirmationDialogText[confirmationDialogAction].body + (active?.name || 'Notification') + '?'}
                    {confirmationDialogAction === 'duplicate_notification' && (
                        <>
                            <Grid item xs={12} mt={2}>
                                <Typography>Choose the type of notification you want to create:</Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <Autocomplete
                                    options={[
                                        { name: 'non_override', id: 0, label: 'Default', value: 0 },
                                        { name: 'override', id: 2, label: 'Tour Specific', value: 2 },
                                    ]}
                                    getOptionLabel={(option) => option.label}
                                    isOptionEqualToValue={(option, value) => option.value === value.value}
                                    onChange={handleNotificationDuplicateTypeChange}
                                    renderInput={(params) => <TextField {...params} variant="filled" color="primary" label="Type" />}
                                />
                            </Grid>
                            {(duplicateSettings.tour_override_mode === 0 || duplicateSettings.tour_override_mode === 1) && (
                                <Grid item xs={12} container>
                                    <Grid item container alignItems="center">
                                        <Radio value={0} name="non_override" checked={duplicateSettings.tour_override_mode === 0} onClick={() => setDuplicateSettings((prev) => ({ ...prev, tour_override_mode: 0 }))} />
                                        <Typography>Non overridable - Will be sent to all tours</Typography>
                                    </Grid>
                                    <Grid item container alignItems="center" justifyContent="flex-start">
                                        <Radio value={1} name="overridable" checked={duplicateSettings.tour_override_mode === 1} onClick={() => setDuplicateSettings((prev) => ({ ...prev, tour_override_mode: 1 }))} />
                                        <Typography>Overridable - Will be sent to all tours except</Typography>
                                        <Typography>the ones that have the same tour-specific notification</Typography>
                                    </Grid>
                                </Grid>
                            )}
                            {duplicateSettings.tour_override_mode === 2 && (
                                <>
                                    <Grid item xs={12} mt={2}>
                                        <Typography>Please choose the tour(s) for the variant:</Typography>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <RemoteAutocomplete filterSelected required InputProps={{ required: true }} resource="tour" label="Select Tour:" multiple variant="filled" size="small" onChange={handleNotificationDuplicateToursChange} value={duplicateSettings.tours} getOptionLabel={(opt) => opt.name} />
                                    </Grid>
                                </>
                            )}
                        </>
                    )}
                </ConfirmationDialog>
            ) : null} */}

            {/* <Grid>
                <Popper
                    open={new URLSearchParams(window?.location?.search).get('creatingNotification') === 'true'}
                    style={{ zIndex: 1, marginTop: '7rem' }}
                    anchorEl={addNotificationAnchorEl}
                    onClose={() => setAddNotificationAnchorEl(null)}
                    placement="bottom-start"
                    disablePortal={false}
                    modifiers={{
                        flip: {
                            enabled: true,
                        },
                        preventOverflow: {
                            enabled: true,
                            boundariesElement: 'scrollParent',
                        },
                    }}
                >
                    <AddNewNotification setLastUpdate={setLastUpdate} setSyncActive={setSyncActive} onClose={handleOnAddNotificationClose} syncActive={syncActive} isSmallScreen={isSmallScreen} />
                </Popper>
            </Grid> */}

            <Grid item xs={12} pt={2} pl={2} pr={2} container justifyContent="space-between" alignItems="center">
                <Typography fontSize={20} fontWeight={500} fontHeight={24}>
                    Rulesets
                </Typography>
                <Grid item xs={'auto'} container direction="row-reverse" alignItems="center">
                    {hasPerm('notification', 'write') && (
                        <Box display="flex" alignItems="center">
                            <Button color="primary" variant="contained" size={isSmallScreen ? 'small' : 'medium'} onClick={(ev) => onAddRuleset(ev)}>
                                <AddIcon />
                                {!isSmallScreen && <>&nbsp;New ruleset</>}
                            </Button>
                            <OutlinedExportButton
                                onClick={onExportList}
                                size={isSmallScreen ? 'small' : 'medium'}
                                hideLabel={isSmallScreen}
                                tooltip="Export list as CSV"
                                sx={{ ml: 1 }}
                            />
                        </Box>
                    )}
                </Grid>

                {/* {isSmallScreen && (
                    <Box position="fixed" right={theme.spacing(3)} bottom={theme.spacing(3)} zIndex={99000}>
                        <Fab color="primary" onClick={(ev) => setAddNotificationAnchorEl(ev.currentTarget)}>
                            <AddIcon />
                        </Fab>
                    </Box>
                )} */}
            </Grid>
            <Grid item xs={12} style={{ margin: '1rem', border: 'solid 1px #D8D8D8', borderRadius: '12px' }}>
                <Grid item xs={12} p={isSmallScreen && 2} pb={0}>
                    {/* <NotificationListToolbar
                        resource={resource}
                        handleCreateNotification={handleCreateNotification}
                        setQuery={props.setRecordListQuery}
                        query={props.recordListQuery}
                        selectedIds={selectedIds}
                        isSmallScreen={isSmallScreen}
                        roleMap={roleMap}
                        tabFilter={tabFilter}
                        recordListFilters={recordListFilters}
                        setRecordListFilters={setRecordListFilters}
                        setLastUpdate={setLastUpdate}
                        setRecordListPageReset={setRecordListPageReset}
                    /> */}
                </Grid>
                <Grid item xs={12} className={classes.recordListWrapper}>
                    <RecordList
                        className={classes.recordList}
                        resource={resource}
                        disableSelectionOnClick={true}
                        // onSelectionModelChange={onSelectionModelChange}
                        // apiFilters={formattedRecordListFilters}
                        query={props.recordListQuery}
                        sortModel={[{ field: 'name', sort: 'asc' }]}
                        deps={lastUpdate}
                        // disableColumnFilter={true}
                        // listItemRenderer={listItemRenderer}
                        disableListBorder={true}
                        checkboxSelection={false}
                        disablePageUrlData={true}
                        // pageReset={recordListPageReset}
                        // rowHeight={125}
                        // relatedLoading={relatedLoading}
                        columns={recordListColumns}
                        noMatchesLabel="No rulesets"
                        perPage={100}
                    // disableColumnMenu={true}
                    />
                </Grid>
            </Grid>
        </div>
    );
};

export default RulesetList;